/**
 * Portal-side fetch wrapper.
 *
 * Employee portal pages (`/portal/*`) use a SEPARATE JWT from admin
 * users — stored under `hf-portal:token` instead of `hf-app:access-token`.
 * Calling `fetchApi` directly from a portal hook sends the ADMIN bearer
 * (or none if the admin is logged out), the BE PortalJwtGuard rejects
 * with 401, and `fetchApi`'s 401-handler used to clear admin storage
 * and force-redirect to /login.
 *
 * This wrapper:
 *   1. Pulls the portal JWT out of localStorage.
 *   2. Forwards through `fetchApi` with `skipAuth: true` so the admin
 *      token isn't injected and the 401-handler doesn't nuke admin auth.
 *   3. Adds the portal Authorization header as a plain string entry —
 *      `new Headers()` would silently lose it because `fetchApi` spreads
 *      the headers as a plain object (`{...headers}`), and Headers
 *      instances don't expose own enumerable properties.
 *
 * Use this for every `/v1/portal/*` (employee-facing) endpoint.
 * Admin-facing portal endpoints like `/v1/portal-news` (workspace CRUD)
 * still go through plain `fetchApi`.
 */

import { fetchApi } from './client';

const PORTAL_TOKEN_KEY = 'hf-portal:token';

function getPortalToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(PORTAL_TOKEN_KEY);
}

export async function fetchPortalApi<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = getPortalToken();
  const existing =
    init.headers instanceof Headers
      ? Object.fromEntries(init.headers.entries())
      : (init.headers as Record<string, string>) ?? {};
  const headers: Record<string, string> = { ...existing };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetchApi<T>(path, { ...init, headers, skipAuth: true });
}
